import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const Login = () => {
    const navigate = useNavigate();
    const { login, adminUser, memberUser, logout } = useApp();
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [errorMessage, setErrorMessage] = useState(''); // New state for inline error

    // Forgot Password State
    const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
    const [forgotMemberId, setForgotMemberId] = useState('');
    const [isResetSent, setIsResetSent] = useState(false);

    // Clear any existing session on mount - REMOVED to allow concurrent sessions
    // If we want to clear forms, we can just reset formData.
    // useEffect(() => {
    //     logout();
    // }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (errorMessage) setErrorMessage(''); // Clear error on typing
    };

    // Redirect when authenticated - MODIFIED to allow concurrent sessions
    // Only redirect if we JUST logged in (triggering the state change)
    // If we mount and contain a user, we show the form but with a notice.
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        try {
            const result = await login(formData.username, formData.password);
            if (!result.success) {
                setErrorMessage(result.message);
            } else {
                // Direct redirect based on the role we just logged in as
                // This bypasses the global priority of adminUser over memberUser

                // TAB ISOLATION: Set the active role for this specific tab
                sessionStorage.setItem('pci_active_role', result.role);

                if (result.role === 'admin') {
                    navigate('/admin', { replace: true });
                } else {
                    navigate('/member', { replace: true });
                }
            }
        } catch (error) {
            setErrorMessage("Login error: " + error.message);
        }
    };

    const handleForgotPasswordSubmit = (e) => {
        e.preventDefault();
        const adminNumber = "919468097665";
        const message = `Halo Admin, I forgot my password. My Customer ID is: ${forgotMemberId}. Please help me reset it.`;
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${adminNumber}?text=${encodedMessage}`;

        // Open WhatsApp in same tab
        window.location.href = whatsappUrl;
        setIsResetSent(true);
    };

    const currentUser = adminUser || memberUser;

    // Helper to get dashboard link
    const dashboardLink = adminUser ? '/admin' : '/member';
    const dashboardName = adminUser ? 'Admin Dashboard' : 'Member Dashboard';

    return (
        <div className="min-h-screen pt-24 pb-12 bg-gray-50 flex flex-col justify-center sm:px-6 lg:px-8 relative">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="text-center">
                    <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary-600/30">
                        <span className="text-white font-bold text-xl">P</span>
                    </div>
                    <h2 className="text-3xl font-black text-gray-900 mb-2">Customer Login</h2>
                    <p className="text-gray-500 mb-8 font-medium">Access your customer dashboard</p>
                </div>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">

                <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-2xl sm:px-10 border border-gray-100">
                    <form className="space-y-6" onSubmit={handleSubmit} autoComplete="off">
                        {/* Username/Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Customer Id</label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    name="username"
                                    required
                                    value={formData.username}
                                    onChange={handleChange}
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm font-medium"
                                    placeholder="Enter your Customer Id"
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Password</label>
                            <div className="mt-1 flex items-center bg-white border border-gray-300 rounded-2xl focus-within:ring-4 focus-within:ring-primary-100 focus-within:border-primary-500 shadow-sm transition-all overflow-hidden relative" style={{ border: '1px solid #d1d5db', borderRadius: '1rem', display: 'flex', alignItems: 'center', backgroundColor: '#fff', position: 'relative' }}>
                                <div className="pl-3 flex items-center pointer-events-none text-gray-400" style={{ paddingLeft: '0.75rem', display: 'flex', alignItems: 'center', color: '#9ca3af' }}>
                                    <Lock className="h-5 w-5" />
                                </div>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="block w-full pl-3 pr-3 py-3.5 border-none focus:ring-0 text-sm font-semibold text-gray-900 placeholder-gray-400 bg-transparent"
                                    style={{ border: 'none', outline: 'none', flex: '1', padding: '0.875rem 0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#111827', backgroundColor: 'transparent' }}
                                    placeholder="Enter your password"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    tabIndex="-1"
                                    className="px-4 flex items-center justify-center text-gray-600 hover:text-gray-900 transition-colors cursor-pointer outline-none bg-transparent"
                                    style={{ padding: '0 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none', height: '100%' }}
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? "Hide Password" : "Show Password"}
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4b5563' }}>
                                            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path>
                                            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path>
                                            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path>
                                            <line x1="2" y1="2" x2="22" y2="22"></line>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4b5563' }}>
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>


                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                                    Remember me
                                </label>
                            </div>

                            <div className="text-sm">
                                <button
                                    type="button"
                                    onClick={() => { setIsForgotPasswordOpen(true); setErrorMessage('') }}
                                    className="font-medium text-primary-600 hover:text-primary-500"
                                >
                                    Forgot password?
                                </button>
                            </div>
                        </div>

                        <div>
                            {errorMessage && (
                                <div className="mb-3 p-2 rounded-lg bg-red-50 border border-red-100 text-center animate-in fade-in slide-in-from-top-1">
                                    <p className="text-xs font-semibold text-red-600">
                                        {errorMessage}
                                    </p>
                                </div>
                            )}
                            <button
                                type="submit"
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all hover:shadow-lg shadow-primary-600/30"
                            >
                                Login <ArrowRight className="ml-2 w-4 h-4" />
                            </button>
                        </div>

                    </form>


                </div>
            </div >



            {/* Forgot Password Modal */}
            {
                isForgotPasswordOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 relative">
                            <button
                                onClick={() => setIsForgotPasswordOpen(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Support Reset</h3>

                            {!isResetSent ? (
                                <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                                    <p className="text-gray-600 text-sm">
                                        Please enter your **Customer Id** below. You will be redirected to WhatsApp to contact Admin for your new password.
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Customer Id</label>
                                        <input
                                            type="text"
                                            required
                                            value={forgotMemberId}
                                            onChange={(e) => setForgotMemberId(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none font-medium"
                                            placeholder="Enter Customer Id (e.g. PC1003)"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full bg-[#25D366] text-white py-3 rounded-lg font-bold hover:bg-[#20bd5c] transition-colors flex items-center justify-center gap-2"
                                        onClick={() => setIsForgotPasswordOpen(false)}
                                    >
                                        Contact Admin on WhatsApp
                                    </button>
                                </form>
                            ) : (
                                <div className="text-center py-6">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600 font-bold text-xl">
                                        ✓
                                    </div>
                                    <h4 className="font-bold text-gray-900 mb-2">WhatsApp Opened</h4>
                                    <p className="text-gray-600 text-sm">
                                        We have opened WhatsApp for you. Please send the message to Admin to reset your password.
                                    </p>
                                    <button
                                        onClick={() => setIsForgotPasswordOpen(false)}
                                        className="mt-6 w-full bg-gray-100 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                                    >
                                        Back to Login
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default Login;
